import type { Portfolio } from "../data/types";

export interface LlmMessage {
  role: "user" | "assistant";
  content: string;
}

export interface LlmStreamEvent {
  type: "text" | "tool_use" | "done" | "error";
  /** text delta payload */
  text?: string;
  /** tool call payload */
  toolName?: string;
  toolInput?: unknown;
  toolId?: string;
  /** error payload */
  message?: string;
}

interface StreamOptions {
  messages: LlmMessage[];
  portfolio: Portfolio;
  signal?: AbortSignal;
  onEvent: (event: LlmStreamEvent) => void;
}

export async function streamChat({
  messages,
  portfolio,
  signal,
  onEvent,
}: StreamOptions): Promise<void> {
  const resp = await fetch("/api/chat", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ messages, portfolio: slimPortfolio(portfolio) }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    onEvent({
      type: "error",
      message: `chat endpoint ${resp.status}`,
    });
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const event = parseSseEvent(raw);
      if (event) onEvent(event);
    }
  }
}

function parseSseEvent(block: string): LlmStreamEvent | null {
  let eventType = "message";
  let data = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) eventType = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    if (eventType === "text") return { type: "text", text: parsed.text };
    if (eventType === "tool_use")
      return {
        type: "tool_use",
        toolName: parsed.name,
        toolInput: parsed.input,
        toolId: parsed.id,
      };
    if (eventType === "done") return { type: "done" };
    if (eventType === "error")
      return { type: "error", message: parsed.message };
  } catch (err) {
    console.warn("[llm] bad sse event", err, block);
  }
  return null;
}

/**
 * Send a compact portfolio snapshot to the server. The full portfolio has
 * per-month rows × 30 merchants which is huge; the system prompt only needs
 * the summary rows + recent events.
 */
function slimPortfolio(p: Portfolio) {
  return {
    now: p.now,
    rolling12moVamp: p.rolling12moVamp,
    weekSecuredEur: p.weekSecuredEur,
    ytdSecuredEur: p.ytdSecuredEur,
    tc40SuppressedGbpWeek: p.tc40SuppressedGbpWeek,
    interventionsWeek: p.interventionsWeek,
    acquirer: p.acquirer,
    merchants: p.merchants.map((m) => ({
      id: m.id,
      name: m.name,
      mcc: m.mcc,
      mccDesc: m.mccDesc,
      status: m.status,
      fraudRatioPct: m.fraudRatioPct,
      peakVampRatio: m.peakVampRatio,
      refundRatePct: m.refundRatePct,
      tcScore: m.tcScore,
      subs: m.subs,
      price: m.price,
      totalVolume: m.totalVolume,
      monthsLive: m.monthsLive,
      refundPolicy: m.refundPolicy,
      cancelEase: m.cancelEase,
      cbMult: m.cbMult,
      terminationDate: m.terminationDate,
    })),
    tc40Recent: p.tc40.slice(-80),
    ethoca: p.ethoca,
    evidenceSummary: {
      total: p.evidence.length,
      byStatus: p.evidence.reduce<Record<string, number>>((acc, e) => {
        acc[e.status] = (acc[e.status] ?? 0) + 1;
        return acc;
      }, {}),
    },
    intelEdges: p.intel,
    activity: p.activity.slice(0, 20),
    siteAudits: p.siteAudits.map((a) => ({
      merchantId: a.merchantId,
      overall: a.overall,
      mccMedianOverall: a.mccMedianOverall,
      flags: a.flags,
      subscores: a.subscores,
    })),
  };
}
